import { useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Grid, Text, HStack, VStack, Button, Icon } from "@chakra-ui/react";
import { LuMessageCircle, LuMessageSquare, LuMail, LuBell, LuPhone } from 'react-icons/lu';
import {
    WhatsAppPreview,
    SMSPreview,
    PushPreview,
    EmailPreview,
    VoicePreview,
    InAppPreview,
} from "../../../TemplateManager/CreateTemplate/ChannelPreviewBox";

import { PRIMARY_BG } from '../../../../theme/brand';
const ENGAGEMENT_COLORS = ['#10b981', '#8b5cf6', PRIMARY_BG, '#3b82f6', '#ef4444'];
const COST_COLORS     = ['#10b981', '#8b5cf6', '#3b82f6', '#f97316', PRIMARY_BG];
const FAILURE_COLORS  = ['#ef4444', '#f97316', '#eab308', '#8b5cf6', '#ec4899'];

const Legend = ({ items, colors }) => (
    <VStack spacing={1} mt={3} align="start">
        {items.map((item, i) => (
            <HStack key={i} spacing={2}>
                <Box w={3} h={3} borderRadius="full" bg={colors[i % colors.length]} flexShrink={0} />
                <Text fontSize="xs" color="gray.500">{item.label}</Text>
                <Text fontSize="xs" fontWeight="medium" ml="auto">{item.value}</Text>
            </HStack>
        ))}
    </VStack>
);

const CHANNELS = [
    { id: 1, name: 'SMS',                icon: LuMessageSquare, color: '#155dfc' },
    { id: 2, name: 'WhatsApp',           icon: LuMessageCircle, color: '#00a63e' },
    { id: 3, name: 'E-Mail',             icon: LuMail,          color: '#9810fa' },
    { id: 4, name: 'Voice (IVR)',        icon: LuPhone,         color: '#f54a00' },
    { id: 5, name: 'In-App Notification',icon: LuBell,          color: '#4f39f6' },
    { id: 6, name: 'Push Notification',  icon: LuBell,          color: '#e60076' },
];

export const MessagePreview = ({ template, selectedChannelIds }) => {
    const ids = selectedChannelIds ?? template?.selectedChannelIds ?? [];
    const channels = CHANNELS.filter(c => ids.includes(String(c.id)));
    const [activeId, setActiveId] = useState(channels[0]?.id ?? null);

    if (!template) return <Text fontSize="sm" color="gray.400" py={8} textAlign="center">No template data</Text>;

    const toHeader = (val) =>
        val && typeof val === 'object' ? val : { type: 'text', content: val ?? '' };

    const renderPreview = () => {
        switch (activeId) {
            case 2:
                return <WhatsAppPreview
                    header={toHeader(template.whatsappHeader)}
                    body={template.whatsappBody}
                    footer={template.whatsappFooter}
                    buttons={template.whatsappButtons}
                    otpType={template.otpType}
                    templateType={template.templateType}
                />;
            case 1:
                return <SMSPreview body={template.smsBody} />;
            case 3:
                return <EmailPreview subject={template.emailSubject} body={template.emailHtml || template.emailTemplate} emailType={template.emailType} />;
            case 6:
                return <PushPreview title={template.pushTitle} body={template.pushBody} imageUrl={template.pushImage} />;
            case 4:
                return <VoicePreview script={template.voiceScript} />;
            case 5:
                return <InAppPreview
                    header={toHeader(template.inAppHeader)}
                    body={template.inAppBody}
                    footer={template.inAppFooter}
                    buttons={template.inAppButtons}
                />;
            default:
                return null;
        }
    };

    return (
        <Box>
            <HStack spacing={1} mb={3} flexWrap="wrap">
                {channels.map(ch => (
                    <Button
                        key={ch.id}
                        size="xs"
                        onClick={() => setActiveId(ch.id)}
                        bg={activeId === ch.id ? ch.color : 'white'}
                        color={activeId === ch.id ? 'white' : 'gray.600'}
                        borderWidth="1px"
                        borderColor={activeId === ch.id ? ch.color : 'gray.200'}
                        _hover={{ bg: activeId === ch.id ? ch.color : 'gray.50' }}
                        borderRadius="full"
                        fontWeight="medium"
                    >
                        <Icon as={ch.icon} boxSize={3} mr={1} />
                        {ch.name}
                    </Button>
                ))}
            </HStack>
            <Box overflowY="auto" maxH="360px">
                {renderPreview()}
            </Box>
        </Box>
    );
};

const CampaignOverview = ({ campaignData }) => {
    if (!campaignData) {
        return (
            <Box p={4} textAlign="center">
                <Text color="gray.500">No overview data available</Text>
            </Box>
        );
    }

    const { engagement, costBreakdown = [], failureReasons = [] ,currency} = campaignData;

    // Engagement donut
    const engagementValues = [
        engagement?.sent ?? 0,
        engagement?.delivered ?? 0,
        engagement?.read ?? 0,
        engagement?.clicked ?? 0,
        engagement?.failed ?? 0,
    ];
    const allEngagementZero = engagementValues.every(v => v === 0);

    const engagementOptions = {
        chart: { type: 'pie', height: 400, backgroundColor: 'transparent' },
        title: { text: 'Engagement Breakdown', align: 'start', style: { fontSize: '14px', fontWeight: '600', color: '#1a202c' } },
        tooltip: { pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)' },
        plotOptions: {
            pie: {
                innerSize: '60%',
                dataLabels: { enabled: false },
                showInLegend: false,
            },
        },
        credits: { enabled: false },
        series: [{
            name: 'Messages',
            colorByPoint: true,
            data: allEngagementZero
                ? [{ name: 'No Data', y: 1, color: '#e2e8f0' }]
                : [
                    { name: 'Sent',      y: engagementValues[0], color: ENGAGEMENT_COLORS[0] },
                    { name: 'Delivered', y: engagementValues[1], color: ENGAGEMENT_COLORS[1] },
                    { name: 'Read',      y: engagementValues[2], color: ENGAGEMENT_COLORS[2] },
                    { name: 'Clicked',   y: engagementValues[3], color: ENGAGEMENT_COLORS[3] },
                    { name: 'Failed',    y: engagementValues[4], color: ENGAGEMENT_COLORS[4] },
                ],
        }],
    };

    // Cost by channel donut
    const activeCosts = costBreakdown.filter(c => c.totalCost > 0);
    const costOptions = {
        chart: { type: 'pie', height: 400, backgroundColor: 'transparent' },
        title: { text: 'Cost Breakdown by Channel', align: 'start', style: { fontSize: '14px', fontWeight: '600', color: '#1a202c' } },
        tooltip: { pointFormat: '<b>${point.y:.4f}</b> ({point.percentage:.1f}%)' },
        plotOptions: {
            pie: {
                innerSize: '0%',
                dataLabels: { enabled: false },
                showInLegend: false,
            },
        },
        credits: { enabled: false },
        series: [{
            name: 'Cost',
            colorByPoint: true,
            data: activeCosts.map((c, i) => ({ name: c.channel, y: c.totalCost, color: COST_COLORS[i % COST_COLORS.length] })),
        }],
    };

    // Failure reasons donut
    const failureOptions = {
        chart: { type: 'pie', height: 400, backgroundColor: 'transparent' },
        title: { text: 'Failure Reasons', align: 'start', style: { fontSize: '14px', fontWeight: '600', color: '#1a202c' } },
        tooltip: { pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)' },
        plotOptions: {
            pie: {
                innerSize: '0%',
                dataLabels: {
                    enabled: true,
                    format: '{point.percentage:.0f}%',
                    style: { fontSize: '11px' },
                },
                showInLegend: false,
            },
        },
        credits: { enabled: false },
        series: [{
            name: 'Count',
            colorByPoint: true,
            data: failureReasons.map((r, i) => ({ name: r.reason, y: r.count, color: FAILURE_COLORS[i % FAILURE_COLORS.length] })),
        }],
    };

    return (
        <Grid templateColumns="repeat(3, 1fr)" gap={4}>
            {/* Engagement Breakdown */}
            <Box className="box-style-card">
                <HighchartsReact highcharts={Highcharts} options={engagementOptions} />
                {allEngagementZero ? (
                    <Text fontSize="sm" color="gray.400" textAlign="center" py={4}>No engagement data yet</Text>
                ) : (
                    <Legend
                        items={[
                            { label: `Pending Delivery (${engagement?.sentPercentage ?? 0}%)`, value: engagementValues[0] },
                            { label: `Delivered (${engagement?.deliveredPercentage ?? 0}%)`,   value: engagementValues[1] },
                            { label: `Read (${engagement?.readPercentage ?? 0}%)`,             value: engagementValues[2] },
                            { label: `Clicked (${engagement?.clickedPercentage ?? 0}%)`,       value: engagementValues[3] },
                            { label: `Failed (${engagement?.failedPercentage ?? 0}%)`,         value: engagementValues[4] },
                        ]}
                        colors={ENGAGEMENT_COLORS}
                    />
                )}
            </Box>

            {/* Cost Breakdown by Channel */}
            <Box className="box-style-card">
                {activeCosts.length > 0 ? (
                    <>
                        <HighchartsReact highcharts={Highcharts} options={costOptions} />
                        <Legend
                            items={activeCosts.map(c => ({
                                label: c.channel,
                                value: `${currency || '$'} ${c.totalCost.toFixed(4)} (${c.messageCount} msgs)`,
                            }))}
                            colors={COST_COLORS}
                        />
                    </>
                ) : (
                    <Box pt={4}>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={6}>Cost Breakdown by Channel</Text>
                        <Text fontSize="sm" color="gray.400" textAlign="center" py={16}>No cost data available</Text>
                    </Box>
                )}
            </Box>

            {/* Failure Reasons */}
            <Box className="box-style-card">
                {failureReasons.length > 0 ? (
                    <>
                        <HighchartsReact highcharts={Highcharts} options={failureOptions} />
                        <Legend
                            items={failureReasons.map(r => ({ label: r.reason, value: r.count }))}
                            colors={FAILURE_COLORS}
                        />
                    </>
                ) : (
                    <Box pt={4}>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={6}>Failure Reasons</Text>
                        <Text fontSize="sm" color="gray.400" textAlign="center" py={16}>No failures recorded</Text>
                    </Box>
                )}
            </Box>
        </Grid>
    );
};

export default CampaignOverview;
